import React, {useContext, useState, useRef} from 'react';
import {useIntl} from 'react-intl';
import DeviceTable from '../../views/device/device-table';
import PermissionTabs from '../../views/permission';
import useStore from '../../hooks/use-store';
import useTimezone from '../../hooks/use-timezone';
import useAlerts from '../../hooks/use-alerts';
import {ModalContext} from '../../apiContexts';
import useServices from '../../hooks/use-services';
import DeviceAdminForm from '../../views/device/device-admin-form';
import {observer} from 'mobx-react';
import AccumulatorForm from '../../views/device/accumulator-form';
import DeviceFines from '../../views/device/device-fines';
import {useCertadocEnabled} from '../../autobahn/features/certadoc/queries';
import useCustomers from '../../hooks/use-customers';
import useGroups from '../../hooks/use-groups';
import useEquipments from '../../hooks/use-equipments';
import useCategories from '../../hooks/use-categories';
import useDevices from '../../hooks/use-devices';
import useUsers from '../../hooks/use-users';
import {formatDateTimeColumn} from '../../helpers/excel-helper';
import {canEditAccumulators} from '../../helpers/accumulator';
import useSaveAccumulator from '../../hooks/use-save-accumulator';

const permissions = ['notification', 'driver', 'geofence', 'maintenance', 'attribute'];

const noCreator = {getUserById: () => undefined};

// Mesmo formato usado para o criador da cerca em geofence-container. Dispositivo cadastrado
// antes de MON-439 nao tem criador registrado e fica em branco.
export const formatDevices = (arr, userStore = noCreator, timezone) => {
    return arr.map((item) => {
        const creator = userStore.getUserById(item.createdByUserId);
        return {
            ...item,
            createdByName: creator ? creator.name : '',
            createdByEmail: creator ? creator.email : '',
            createdAtFormatted: formatDateTimeColumn(item.createdAt, timezone),
            updatedAtFormatted: formatDateTimeColumn(item.updatedAt, timezone),
        };
    });
};

const DeviceContainer = () => {
    const {store} = useStore();
    const {timezone} = useTimezone();
    const {toast} = useAlerts();
    const intl = useIntl();
    const {deviceService} = useServices();
    const {showModalLoading, isModalLoading} = useContext(ModalContext);
    const {
        auth,
        session,
    } = store;

    const {customers: customerStore} = useCustomers();
    const {groupStore} = useGroups();
    const {equipmentStore} = useEquipments();
    const {categoryStore} = useCategories();
    const {devices: deviceStore} = useDevices();
    const {users: userStore} = useUsers();
    const certadocEnabled = useCertadocEnabled();

    const selectedDeviceData = useRef(null);
    const [view, setView] = useState('deviceList');
    const [selectedDevice, setSelectedDevice] = useState(null);

    const newRegister = async (data, images) => {
        try {
            showModalLoading(true);
            await deviceStore.createDeviceRemote(data, images);
            setView('deviceList');
            toast.success(intl.formatMessage({id: 'sharedSavedSuccess'}));
        } catch (e) {
            toast.errorStatus(e);
        } finally {
            showModalLoading(false);
        }
    };

    const updateRegister = async (data, images = null) => {
        try {
            showModalLoading(true);
            await deviceStore.updateDeviceRemote(data, images);
            setView('deviceList');
            toast.success(intl.formatMessage({id: 'sharedUpdatedSuccess'}));
            selectedDeviceData.current = null;
        } catch (e) {
            toast.errorStatus(e, {legacy: false});
        } finally {
            showModalLoading(false);
        }
    };

    const removeRegister = async (data) => {
        try {
            showModalLoading(true);
            await deviceService.remove(data.id);
            deviceStore.removeDevice(data.id);
            setView('deviceList');
            toast.success(intl.formatMessage({id: 'sharedRemovedSuccess'}));
        } catch (e) {
            toast.errorStatus(e);
        } finally {
            showModalLoading(false);
        }
    };

    const showPermissionTabs = (data) => {
        setSelectedDevice({deviceId: data.id});
        setView('devicePermissions');
    };

    const {save: saveAccumulatorRemote, saving: savingAccumulator} = useSaveAccumulator();

    const saveAccumulator = async (accumulator) => {
        if (await saveAccumulatorRemote(accumulator)) {
            setView('deviceList');
            selectedDeviceData.current = null;
        }
    };

    const onPressAdd = () => {
        setView('deviceForm');
    };

    const onPressEdit = (_selectedDeviceData) => {
        selectedDeviceData.current = _selectedDeviceData;
        setView('deviceForm');
    };

    // O formulario de odometro parte da posicao atual, que a linha da tabela nao carrega: ela so
    // traz cadastro, para nao ser reconstruida a cada posicao recebida.
    const onPressAccumulator = (_selectedDeviceData) => {
        selectedDeviceData.current =
            deviceStore.snapshotById(_selectedDeviceData.id) ?? _selectedDeviceData;
        setView('accumulatorForm');
    };

    const onPressFormCancel = () => {
        setView('deviceList');
        selectedDeviceData.current = null;
    };

    const onPressAccumulatorCancel = () => {
        setView('deviceList');
        selectedDeviceData.current = null;
    };

    const onPressFines = (_selectedDeviceData) => {
        selectedDeviceData.current = _selectedDeviceData;
        setView('certadocFines');
    };

    const onPressFinesCancel = () => {
        setView('deviceList');
        selectedDeviceData.current = null;
    };

    const onPressDevicePermissionsCancel = () => {
        setView('deviceList');
        setSelectedDevice(null);
    };

    // Cada valor vira thunk, e so o da view ativa e chamado: um objeto literal comum avalia
    // toda propriedade em todo render, entao o `deviceStore.list` da chave `deviceList` seria
    // lido (e assinado pelo observer) mesmo com o usuario parado em outra view. Ver MON-522.
    return (
        <>
            {
                {
                    deviceList: () => (
                        <DeviceTable
                            auth={auth}
                            isAdministrator={!!session.administrator}
                            list={formatDevices(deviceStore.registrationList, userStore, timezone)}
                            excelData={() => formatDevices(deviceStore.list, userStore, timezone)}
                            onPressAdd={onPressAdd}
                            onPressEdit={onPressEdit}
                            onPressAccumulator={canEditAccumulators(session) ? onPressAccumulator : undefined}
                            onPressFines={certadocEnabled ? onPressFines : undefined}
                            removeRegister={removeRegister}
                            permissions={showPermissionTabs}
                            loading={isModalLoading}
                            timezone={timezone}
                            intl={intl}
                        />
                    ),
                    deviceForm: () => (
                        <DeviceAdminForm
                            cancel={onPressFormCancel}
                            data={Object.assign({}, selectedDeviceData.current)}
                            groups={groupStore.listAsOptions}
                            categories={categoryStore.listAsOptions}
                            equipments={equipmentStore.listAsOptions}
                            edit={!!selectedDeviceData.current}
                            newRegister={newRegister}
                            updateRegister={updateRegister}
                            loading={isModalLoading}
                            customers={customerStore.listAsOptions}
                            session={session}
                            deviceStore={deviceStore}
                        />
                    ),
                    devicePermissions: () => (
                        <PermissionTabs
                            options={permissions}
                            data={selectedDevice}
                            cancel={onPressDevicePermissionsCancel}/>
                    ),
                    accumulatorForm: () => (
                        <AccumulatorForm
                            data={selectedDeviceData.current}
                            saveAccumulator={saveAccumulator}
                            loading={savingAccumulator}
                            cancel={onPressAccumulatorCancel}/>
                    ),
                    certadocFines: () => (
                        <DeviceFines
                            data={selectedDeviceData.current}
                            cancel={onPressFinesCancel}/>
                    ),
                }[view]()
            }
        </>
    );
};

export default observer(DeviceContainer);

